import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { CodeNameModel } from 'components/common/types';
import { NavigationItem } from 'components/common/types/NavigationItem.types';
import { Transition } from 'consts/transition';
import { POI } from 'components/common/types/Planner.types';
import { ProductFieldName } from 'store/productManagement/reducer.types';
import { changeNewProductField, hideSecondaryPanel } from 'store/productManagement/reducer';
import { usePOIData } from 'components/pages/Planner/PlannerSections/DataTargetingContent/PointsOfInterest/usePOIData';
import { usePOI } from 'components/pages/Planner/PlannerSections/DataTargetingContent/PointsOfInterest/usePOI';
import searchOptions from 'utils/searchOptions';
import { isValueRequired } from '../../utils/isValueRequired';
import { getCurrentPoiOptions, initializeDataProvider } from './ProductPoiPanel.utils';
import { UseProductPoiPanelProps, UseProductPoiPanelReturn } from './ProductPoi.types';
import { PCM_CUSTOM_POI_MAX_ROWS } from './consts';

export const useProductPoiPanel = ({
  selectedPOIs,
  selectedPermission,
  productPoi,
}: UseProductPoiPanelProps): UseProductPoiPanelReturn => {
  const dispatch = useDispatch();
  const fieldName = ProductFieldName.POI;

  const [localSelectedPOIs, setLocalSelectedPOIs] = useState<POI[]>(selectedPOIs);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [navigationStack, setNavigationStack] = useState<NavigationItem[]>([]);
  const [transition, setTransition] = useState<Transition>(Transition.NONE);

  const { clearSearchResults, searchPoi, searchTerm, dataProvidersWithPoiOptions } = usePOIData();

  const {
    currentFocusedItemIndex,
    getSelectedPOIIncludeAndRadius,
    isItemSelected,
    onChangeRadius,
    onIncludeExcludeChange,
    resetCurrentFocusedItemIndex,
    resetPOIToPreviousState,
    getSelectedPOIIncludedExcludedChildrenCounter,
    isSelectedPOIChanged,
    handleConfirm,
    customPoiCategories,
    onFileChange,
    onDeleteCustomCategory,
    hasReachedFileUploadLimit,
    isCustomCategory,
    isInsideCustomCategory,
  } = usePOI({
    pois: localSelectedPOIs,
    selectedProvider,
    onConfirm: setLocalSelectedPOIs,
    numberOfAllowedPOIs: 0,
    navigationStack,
    customPoiMaxRows: PCM_CUSTOM_POI_MAX_ROWS,
  });

  const filteredCustomPoiCategories = useMemo(
    () => (searchTerm ? searchOptions(customPoiCategories, searchTerm) : customPoiCategories),
    [customPoiCategories, searchTerm],
  );

  useEffect(() => {
    resetCurrentFocusedItemIndex();
  }, [searchTerm]);

  useEffect(() => {
    if (isSelectedPOIChanged) {
      handleConfirm();
    }
  }, [isSelectedPOIChanged, handleConfirm]);

  useEffect(() => {
    if (!isValueRequired(selectedPermission.code)) {
      setLocalSelectedPOIs([]);
    }
  }, [selectedPermission.code]);

  const dataProviders = useMemo(() => {
    return Object.keys(dataProvidersWithPoiOptions ?? {});
  }, [dataProvidersWithPoiOptions]);

  const currentScreen = navigationStack[navigationStack.length - 1];
  const isMainLevel = navigationStack.length === 0;

  useEffect(() => {
    initializeDataProvider(dataProviders, setSelectedProvider);
  }, [dataProviders]);

  const handleSearchChange = useCallback(
    (value: string): void => {
      if (value.length > 0) {
        searchPoi({ text: value, dataProvider: selectedProvider });
      } else {
        clearSearchResults();
      }
    },
    [selectedProvider, searchPoi, clearSearchResults],
  );

  const handleProviderSelect = useCallback(
    (_: string, provider: CodeNameModel): void => {
      setSelectedProvider(provider.code);
      setNavigationStack([]);
      clearSearchResults();
    },
    [clearSearchResults],
  );

  const navigateForward = useCallback((title: string, categoryPath?: string): void => {
    setTransition(Transition.FORWARD);
    setNavigationStack((prev: NavigationItem[]) => [...prev, { title, categoryPath }]);
  }, []);

  const navigateBack = useCallback((): void => {
    setTransition(Transition.BACKWARD);
    setNavigationStack(navigationStack.slice(0, -1));
  }, [navigationStack]);

  const onTransitionComplete = useCallback((): void => {
    setTransition(Transition.NONE);
  }, []);

  const currentPoiOptions = useMemo(() => {
    const dataProviderOptions = getCurrentPoiOptions(
      selectedProvider,
      dataProvidersWithPoiOptions,
      searchTerm,
      navigationStack,
    );

    if (isMainLevel) {
      return filteredCustomPoiCategories.length > 0
        ? [...filteredCustomPoiCategories, ...dataProviderOptions]
        : dataProviderOptions;
    }

    const allOptions =
      filteredCustomPoiCategories.length > 0
        ? [...filteredCustomPoiCategories, ...(dataProvidersWithPoiOptions?.[selectedProvider] || [])]
        : dataProvidersWithPoiOptions?.[selectedProvider] || [];

    return navigationStack.reduce(
      (options, navItem) => options.find((option) => option.name === navItem.title)?.children || [],
      allOptions,
    );
  }, [
    searchTerm,
    selectedProvider,
    dataProvidersWithPoiOptions,
    navigationStack,
    filteredCustomPoiCategories,
    isMainLevel,
  ]);

  const displayedPoiOptions = useMemo(() => {
    if (!isMainLevel) return currentPoiOptions;
    return currentPoiOptions.filter((option) => !isCustomCategory(option));
  }, [currentPoiOptions, isMainLevel, isCustomCategory]);

  const onSubmit = useCallback((): void => {
    dispatch(
      changeNewProductField({
        fieldName,
        selectedPermission,
        value: isValueRequired(selectedPermission.code) ? localSelectedPOIs : undefined,
      }),
    );
    dispatch(hideSecondaryPanel());
  }, [dispatch, fieldName, selectedPermission, localSelectedPOIs]);

  const handleCancel = useCallback(
    (onCancel: () => void): void => {
      if (!isMainLevel) {
        navigateBack();

        return;
      }

      resetPOIToPreviousState();
      setLocalSelectedPOIs(selectedPOIs);
      onCancel();
    },
    [isMainLevel, navigateBack, resetPOIToPreviousState, selectedPOIs],
  );

  const isChange = useMemo(
    () => JSON.stringify(selectedPOIs) !== JSON.stringify(localSelectedPOIs),
    [selectedPOIs, localSelectedPOIs],
  );

  return {
    localSelectedPOIs,
    selectedProvider,
    navigationStack,
    transition,
    currentScreen,
    isMainLevel,
    dataProviders,
    dataProvidersWithPoiOptions,
    searchTerm,
    currentPoiOptions: displayedPoiOptions,
    currentFocusedItemIndex,
    getSelectedPOIIncludeAndRadius,
    isItemSelected,
    onChangeRadius,
    onIncludeExcludeChange,
    resetCurrentFocusedItemIndex,
    getSelectedPOIIncludedExcludedChildrenCounter,
    handleSearchChange,
    handleProviderSelect,
    navigateForward,
    onTransitionComplete,
    onSubmit,
    handleCancel,
    isChange,
    productPoi,
    customPoiCategories: filteredCustomPoiCategories,
    onFileChange,
    onDeleteCustomCategory,
    hasReachedFileUploadLimit,
    isInsideCustomCategory,
  };
};
